import React, { useState, useEffect } from "react";
import { Form, Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import FormInput from "src/components/Common/Form/FormInput";
import FormSelect from "src/components/Common/Form/FormSelect";
import { useForm } from "react-hook-form";
import { getTestAnalysisProcess, createTestAnalysis } from "src/services/nal-test-analysis";
import { useMutation } from "src/Hooks/useReactQuery";
import { transformProcessDataToOptions, getConfigFromId } from "src/utils/common";
import { PATTERN_NUMBER, VALIDATE_RULE_BY_FIELD } from "src/constant/common";
import ProtectedComponent from "src/components/Permission";

const FORM_NAME = {
  NAME: "name",
  PROCESS_ID: "process_id",
  representative_surface_areas: "representative_surface_areas",
  representative_length: "representative_length",
  test_airflow_settling: "test_airflow_settling",
  base_area: "base_area",
  cavity_area: "cavity_area",
  angle_of_attack_classification_unit: "angle_of_attack_classification_unit",
  min_angle_of_attack: "min_angle_of_attack",
  max_angle_of_attack: "max_angle_of_attack",
  error_range_of_angle_of_attack: "error_range_of_angle_of_attack",
};

const AddTestAnalysis = ({ onSuccess }) => {
  const { t } = useTranslation("wind-tunnel-test-nal");
  const [toggle, setToggle] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [config, setConfig] = useState([]);

  const { data: options, mutate: getOptionsMutate } = useMutation({
    mutationFn: () => getTestAnalysisProcess(),
    onSettled: () => setIsLoading(false),
  });

  const createTestAnalysisMutate = useMutation({
    mutationFn: ({ data }) => createTestAnalysis({ data }),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitted },
    reset,
    // setValue,
    watch,
    trigger,
    // setError,
    // clearErrors,
  } = useForm({});

  const processId = watch(FORM_NAME.PROCESS_ID);
  const trackingField = {};
  trackingField[FORM_NAME.min_angle_of_attack] = watch(FORM_NAME.min_angle_of_attack);
  trackingField[FORM_NAME.max_angle_of_attack] = watch(FORM_NAME.max_angle_of_attack);

  const handleSubmitForm = (data) => {
    const inputData = {
      name: data.name.trim(),
      process_id: data.process_id,
      config: {},
    };

    for (const item of config) {
      inputData.config[item.name] = item.type === "number" ? parseFloat(data[item.name]) : data[item.name];
    }

    setIsSubmit(true);
    createTestAnalysisMutate.mutate(
      { data: inputData },
      {
        onSuccess: () => {
          setToggle(false);
          onSuccess();
        },
        onSettled: () => {
          setIsSubmit(false);
        },
      }
    );
  };

  useEffect(() => {
    if (processId) {
      setConfig(getConfigFromId(processId, options, "test_analysis"));
    }
  }, [processId]);

  useEffect(() => {
    if (isSubmitted && trackingField[FORM_NAME.min_angle_of_attack]) {
      trigger(FORM_NAME.max_angle_of_attack);
    }
  }, [trackingField[FORM_NAME.min_angle_of_attack]]);

  useEffect(() => {
    if (isSubmitted && trackingField[FORM_NAME.max_angle_of_attack]) {
      trigger(FORM_NAME.min_angle_of_attack);
    }
  }, [trackingField[FORM_NAME.max_angle_of_attack]]);

  useEffect(() => {
    getOptionsMutate();
  }, []);

  return (
    <>
      <ProtectedComponent id={`add_test_analysis`}>
        <button
          onClick={() => {
            reset();
            setConfig([]);
            setToggle(true);
          }}
          className="btn btn-primary text-capitalize"
        >
          {t("list.redirect_new")}
        </button>
      </ProtectedComponent>
      <Modal
        centered
        isOpen={toggle}
        toggle={() => setToggle(false)}
        size="lg"
        backdrop="static"
        style={{ maxWidth: "1000px", width: "85%" }}
      >
        <ModalHeader>{t("new.title")}</ModalHeader>
        <ModalBody className="p-b-0 overflow-auto">
          <Form className="p-x-15">
            <div className="row">
              <div className="col-md-12">
                <FormInput
                  register={register}
                  label={t("new.name")}
                  name={FORM_NAME.NAME}
                  placeholder={t("new.name_placeholder")}
                  errors={errors?.[FORM_NAME.NAME]?.message}
                  requiredMessage={t("new.name_required")}
                  required
                  maxLength={255}
                  maxLengthMessage={t("new.name_maxlength")}
                />
                {isLoading ? (
                  <Spinner size="sm" />
                ) : (
                  <FormSelect
                    label={t("new.process")}
                    name={FORM_NAME.PROCESS_ID}
                    placeholder={t("new.process_placeholder")}
                    control={control}
                    errors={errors?.[FORM_NAME.PROCESS_ID]?.message}
                    required
                    options={transformProcessDataToOptions(options || [])}
                    requiredMessage={t("new.process_required")}
                  />
                )}

                {config.map((item, index) => {
                  if (FORM_NAME?.[item.name]) {
                    const validateRules = VALIDATE_RULE_BY_FIELD(t)[item.name];
                    return (
                      <FormInput
                        key={index}
                        register={register}
                        type={item.type}
                        pattern={
                          item.type === "number"
                            ? {
                                value: PATTERN_NUMBER,
                                message: t(`new.${item.name}_validate_${item.type}`),
                              }
                            : null
                        }
                        label={!validateRules?.notShowLabel && t(`new.${item.name}`)}
                        name={FORM_NAME[item.name]}
                        placeholder={t(`new.${item.name}_placeholder`)}
                        errors={errors?.[FORM_NAME[item.name]]?.message}
                        requiredMessage={t(`new.${item.name}_required`)}
                        minEqual={trackingField[validateRules?.min?.field] || null}
                        minMessage={validateRules?.min?.msg}
                        maxEqual={trackingField[validateRules?.max?.field] || null}
                        unit={validateRules?.unit || null}
                        maxMessage={validateRules?.max?.msg}
                        valueGreaterThanZero={item.type === "number" && !validateRules?.allowNegative}
                        classGroup={validateRules?.customClass || ""}
                        required
                      />
                    );
                  }
                  return "";
                })}
              </div>
            </div>
          </Form>
        </ModalBody>
        <ModalFooter className="justify-content-center p-t-10 p-b-20">
          <button
            className="btn btn-sm btn-secondary m-x-10 width-by-px-120"
            onClick={() => {
              setToggle(false);
              document.body.click();
            }}
          >
            {t("new.cancel")}
          </button>
          <button
            className="btn btn-sm btn-primary m-x-10 width-by-px-120"
            disabled={isSubmit}
            onClick={handleSubmit((data) => handleSubmitForm(data))}
          >
            {t("new.register")}
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default AddTestAnalysis;
